import { useLocale, useTranslations } from "next-intl";
import moment from "moment-timezone";
import "moment/locale/fr";
import "moment/locale/en-gb";
import Image from "next/image";
import { useState } from "react";

export default function LastSevenDaysProgress({
    // Valeurs par défaut obligatoires : ce panneau est rendu depuis la barre du
    // haut, avec une session qui peut être un instantané incomplet (voir le
    // commentaire de Streak.js). Un `activity` absent faisait planter le rendu
    // de toute la page sur la première lecture d'index.
    activity = {},
    userTimezone,
    streakCount = 0,
    shieldedDates = [],
    expanded = false,
}) {
    const locale = useLocale();
    const t = useTranslations();
    const [hoveredDay, setHoveredDay] = useState(null);

    const getLast7Days = () => {
        const days = [];
        for (let i = 6; i >= 0; i--) {
            const date = moment()
                .tz(userTimezone)
                .locale(locale)
                .subtract(i, "days");
            days.push({
                day: date.format("dd").charAt(0),
                fullDay: date.format("dddd"),
                formattedDate: date.format("LL"),
                date: date.format("YYYY-MM-DD"),
                isToday: i === 0,
                isYesterday: i === 1,
                daysAgo: i,
            });
        }
        return days;
    };

    const last7Days = getLast7Days();
    const todayDate = last7Days[last7Days.length - 1].date;
    const todayHasActivity = (activity[todayDate] || 0) > 0;
    const streakAtRisk = streakCount > 0 && !todayHasActivity;

    const maxActivity = Math.max(
        ...last7Days.map((d) => activity[d.date] || 0),
        1,
    );
    const activeDaysCount = last7Days.filter(
        (d) => (activity[d.date] || 0) > 0,
    ).length;

    // Jours protégés par un bouclier :
    // Priorité aux données exactes du backend (shieldedDates),
    // sinon déduction depuis la streak et l'activité
    const isShieldedDay = (date, daysAgo, hasActivity) => {
        if (hasActivity || daysAgo === 0) return false;
        if (shieldedDates && shieldedDates.length > 0) {
            return shieldedDates.includes(date);
        }
        return streakCount > 0 && daysAgo < streakCount;
    };

    return (
        <div className="mt-4 flex-1 flex flex-col">
            {/* Bar chart */}
            <div className={`relative ${expanded ? "flex-1" : ""}`}>
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-100 dark:bg-white/10" />
                <div className={`flex items-end gap-2.5 ${expanded ? "h-full min-h-[72px]" : "h-[72px]"}`}>
                    {last7Days.map(
                        (
                            {
                                day,
                                fullDay,
                                formattedDate,
                                date,
                                isToday,
                                isYesterday,
                                daysAgo,
                            },
                            index,
                        ) => {
                            const isHovered = hoveredDay === index;
                            const value = activity[date] || 0;
                            const hasActivity = value > 0;
                            const shielded = isShieldedDay(
                                date,
                                daysAgo,
                                hasActivity,
                            );
                            const heightPercent = hasActivity
                                ? Math.max((value / maxActivity) * 100, 24)
                                : shielded
                                ? 28
                                : 10;

                            return (
                                <div
                                    key={index}
                                    className="flex-1 flex flex-col items-center justify-end h-full relative cursor-pointer"
                                    onMouseEnter={() => setHoveredDay(index)}
                                    onMouseLeave={() => setHoveredDay(null)}
                                >
                                    {/* Tooltip */}
                                    {isHovered && (
                                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-40 pointer-events-none">
                                            <div className="bg-gray-900 dark:bg-grayDark-700 text-white px-3 py-2 rounded-xl shadow-lg whitespace-nowrap text-center">
                                                <div className="text-xs font-semibold capitalize">
                                                    {fullDay}
                                                </div>
                                                <div className="text-[10px] text-gray-400 mt-0.5">
                                                    {formattedDate}
                                                </div>
                                                {isToday && (
                                                    <div className="text-[10px] text-amber-400 font-medium mt-0.5">
                                                        {t("activity_today")}
                                                    </div>
                                                )}
                                                {isYesterday && (
                                                    <div className="text-[10px] text-blue-400 font-medium mt-0.5">
                                                        {t(
                                                            "activity_yesterday",
                                                        )}
                                                    </div>
                                                )}
                                                {shielded && (
                                                    <div className="text-[10px] text-blue-300 font-medium mt-0.5">
                                                        {t(
                                                            "activity_shielded",
                                                        )}
                                                    </div>
                                                )}
                                                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2">
                                                    <div className="w-2 h-2 bg-gray-900 dark:bg-grayDark-700 rotate-45" />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Shield day: icon + colored background */}
                                    {shielded && (
                                        <div className="flex-1 w-full flex items-center justify-center rounded-t-lg bg-gradient-to-t from-blue-100 to-blue-50 dark:from-blue-500/15 dark:to-blue-400/5 border border-blue-200/60 dark:border-blue-500/20 border-b-0">
                                            <Image
                                                src="/static/icons/shield.svg"
                                                width={18}
                                                height={18}
                                                alt="Shield"
                                                className="opacity-80"
                                            />
                                        </div>
                                    )}

                                    {/* Bar */}
                                    {!shielded && (
                                        <div
                                            className={`w-full rounded-t-lg rounded-b-sm transition-all duration-500 ease-out ${
                                                hasActivity
                                                    ? "bg-gradient-to-t from-primary to-primary-100"
                                                    : isToday
                                                    ? "bg-animate"
                                                    : "bg-gray-100 dark:bg-white/10"
                                            } ${isHovered && hasActivity ? "brightness-110" : ""}`}
                                            style={{
                                                height: `${heightPercent}%`,
                                                minHeight: hasActivity
                                                    ? "16px"
                                                    : "6px",
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        },
                    )}
                </div>
            </div>

            {/* Day labels with active/shield dots */}
            <div className="flex gap-2.5 mt-2.5">
                {last7Days.map(({ day, date, isToday, daysAgo }, index) => {
                    const hasActivity = (activity[date] || 0) > 0;
                    const shielded = isShieldedDay(date, daysAgo, hasActivity);
                    return (
                        <div
                            key={index}
                            className="flex-1 flex flex-col items-center gap-1"
                        >
                            <span
                                className={`text-[11px] uppercase leading-none ${
                                    hasActivity
                                        ? "text-primary font-bold"
                                        : shielded
                                        ? "text-blue-400 font-semibold"
                                        : isToday
                                        ? "text-amber-500 font-semibold"
                                        : "text-gray-400 dark:text-grayDark-400 font-medium"
                                }`}
                            >
                                {day}
                            </span>
                            {hasActivity && (
                                <div className="w-1 h-1 rounded-full bg-primary" />
                            )}
                            {shielded && (
                                <div className="w-1 h-1 rounded-full bg-blue-400" />
                            )}
                        </div>
                    );
                })}
            </div>

        </div>
    );
}
